import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/context/SettingsContext";
import { toast } from "sonner";
import { Power, ChevronDown, Activity, LogOut, User } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const STORES = [
  { key: "all", label: "All Stores" },
  { key: "shopify", label: "Shopify Main" },
  { key: "printify", label: "Printify Hub" },
  { key: "etsy", label: "Etsy Studio" },
];

export default function TopBar({ pendingCount = 0 }) {
  const { user, logout } = useAuth();
  const { settings, toggleKill, store, setStore } = useSettings();
  const [confirm, setConfirm] = useState(false);
  const paused = settings?.kill_switch;

  const handleKill = async () => {
    const next = !paused;
    await toggleKill(next);
    setConfirm(false);
    if (next) toast.error("All agents paused. Publishing & ad spend halted.");
    else toast.success("Agents resumed. Autonomy restored.");
  };

  const storeLabel = STORES.find((s) => s.key === store)?.label || "All Stores";

  return (
    <header className="fixed top-0 right-0 left-64 h-16 bg-[#090B0E]/90 backdrop-blur-md border-b border-slate-800/80 z-30 flex items-center justify-between px-6">
      <div className="flex items-center gap-4">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button data-testid="store-selector-dropdown" className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-[#12161F] border border-slate-800 text-sm text-slate-200 hover:border-slate-700 transition-colors">
              <span className="w-2 h-2 rounded-full bg-emerald-400 shadow-[0_0_8px_rgba(16,185,129,0.7)]" />
              {storeLabel}
              <ChevronDown className="w-4 h-4 text-slate-500" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="bg-[#12161F] border-slate-800 text-slate-100">
            {STORES.map((s) => (
              <DropdownMenuItem key={s.key} data-testid={`store-option-${s.key}`} onClick={() => setStore(s.key)} className="cursor-pointer focus:bg-slate-800">
                {s.label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        <div className="hidden md:flex items-center gap-2 px-3 py-1.5 rounded-lg bg-[#12161F]/60 border border-slate-800/70 text-xs font-mono">
          <Activity className={`w-3.5 h-3.5 ${paused ? "text-red-400" : "text-emerald-400"}`} />
          <span className="text-slate-400">
            {paused ? "AGENTS PAUSED" : "4 AGENTS ACTIVE"} · <span className="text-amber-300">{pendingCount} PENDING</span>
          </span>
        </div>
      </div>

      <div className="flex items-center gap-3">
        <button
          data-testid="kill-switch-button"
          onClick={() => setConfirm(true)}
          className={`px-4 py-2 rounded-lg font-bold text-xs uppercase tracking-wider flex items-center gap-2 border transition-all duration-300 ${
            paused
              ? "bg-red-600 text-white border-red-400 shadow-[0_0_25px_rgba(239,68,68,0.7)] animate-pulse"
              : "bg-red-950/50 text-red-400 border-red-800/80 hover:bg-red-900 hover:text-white shadow-[0_0_12px_rgba(239,68,68,0.2)]"
          }`}
        >
          <Power className="w-4 h-4" />
          {paused ? "Resume Agents" : "Pause All Agents"}
        </button>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button data-testid="user-menu-button" className="w-9 h-9 rounded-full bg-emerald-500/20 border border-emerald-500/40 flex items-center justify-center text-emerald-300 hover:bg-emerald-500/30 transition-colors">
              <User className="w-4 h-4" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="bg-[#12161F] border-slate-800 text-slate-100 w-52">
            <div className="px-2 py-1.5 text-xs text-slate-400">
              <div className="text-slate-200 font-medium truncate">{user?.name}</div>
              <div className="truncate">{user?.email}</div>
            </div>
            <DropdownMenuSeparator className="bg-slate-800" />
            <DropdownMenuItem data-testid="logout-button" onClick={logout} className="cursor-pointer focus:bg-slate-800 text-red-300">
              <LogOut className="w-4 h-4 mr-2" /> Log out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <AlertDialog open={confirm} onOpenChange={setConfirm}>
        <AlertDialogContent className="bg-[#12161F] border-slate-800 text-slate-100">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-head">{paused ? "Resume all agents?" : "Pause all agents?"}</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              {paused
                ? "Autonomous research, design, listing and approved publishing will resume immediately."
                : "This immediately halts all autonomous execution: no publishing, no ad spend, no new agent actions until you resume."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-slate-700 text-slate-300 hover:bg-slate-800">Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-kill-switch-button"
              onClick={handleKill}
              className={paused ? "bg-emerald-500 text-slate-950 hover:bg-emerald-400" : "bg-red-600 text-white hover:bg-red-700"}
            >
              {paused ? "Resume Agents" : "Pause Everything"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </header>
  );
}
